/* Quadrajet Power — Package Protection
   Auto-adds a protection line priced from hidden tier variants (nearest tier).
   Pricing follows the self-insured model set in Theme settings:
     eligible subtotal below flatThreshold -> flatFee   (default $1.00 under $40)
     eligible subtotal at or above it      -> rate %    (default 2.5%)
   Opt-out is remembered for the cart session via the
   _package_protection_opted_out cart attribute. */
(function () {
  'use strict';
  var cfgEl = document.querySelector('[data-pp-config]');
  if (!cfgEl || !window.fetch) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  if (!cfg || !cfg.handle || !cfg.variants || !cfg.variants.length) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var OPT_OUT_ATTR = '_package_protection_opted_out';
  // Tier sanity: with only the placeholder variant we can't price correctly.
  // Per spec, disable the widget rather than charging a wrong amount.
  var provisioned = cfg.variants.length >= 5;
  if (!provisioned) {
    console.error('[package-protection] Pricing tier variants are not provisioned on "' + cfg.handle + '" (found ' + cfg.variants.length + '). Widget disabled.');
    return;
  }
  var variants = cfg.variants.slice().sort(function (a, b) { return a.price - b.price; });
  // Flat-fee band is active only when both values are present and positive.
  var FLAT_THRESHOLD = (typeof cfg.flatThreshold === 'number' && cfg.flatThreshold > 0) ? cfg.flatThreshold : 0;
  var FLAT_FEE = (typeof cfg.flatFee === 'number' && cfg.flatFee > 0) ? cfg.flatFee : 0;
  var flatBand = FLAT_THRESHOLD > 0 && FLAT_FEE > 0;
  var busy = false;
  var timer = null;
  var lastCart = null;

  function eligibleSubtotal(cart) {
    var total = 0;
    cart.items.forEach(function (it) {
      if (it.handle === cfg.handle) return;
      if (it.gift_card) return;
      if (!it.requires_shipping) return;
      total += it.final_line_price;
    });
    return total;
  }
  function protectionLines(cart) {
    return cart.items.filter(function (it) { return it.handle === cfg.handle; });
  }
  function protectionLine(cart) {
    var lines = protectionLines(cart);
    return lines.length ? lines[0] : null;
  }
  function feeAmount(subtotalCents) {
    if (flatBand && subtotalCents < FLAT_THRESHOLD) return FLAT_FEE;
    return Math.round(subtotalCents * (cfg.rate / 100));
  }
  function feeVariant(subtotalCents) {
    var fee = feeAmount(subtotalCents);
    var best = variants[0];
    var bestDiff = Math.abs(variants[0].price - fee);
    variants.forEach(function (v) {
      var d = Math.abs(v.price - fee);
      if (d < bestDiff) { best = v; bestDiff = d; }
    });
    return best;
  }
  function optedOut(cart) {
    return cart.attributes && String(cart.attributes[OPT_OUT_ATTR] || '') === '1';
  }

  function api(path, payload) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) throw new Error('[package-protection] ' + path + ' failed (' + r.status + ')');
      return r.json();
    });
  }
  var failCount = 0;
  var MAX_FAILURES = 2;
  var onCartPage = document.body.classList.contains('template-cart');

  function redraw() {
    if (onCartPage) {
      // The cart page is server-rendered; only a reload keeps its rows,
      // totals, and native form inputs consistent with the mutation.
      window.location.reload();
      return Promise.resolve();
    }
    if (typeof window.QJPRefreshCartDrawer === 'function') return window.QJPRefreshCartDrawer();
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { lastCart = cart; renderWidgets(cart); });
  }

  /* Compute the single mutation (if any) that moves `cart` toward the
     desired state. Handles duplicate protection lines atomically. */
  function computeMutation(cart) {
    var lines = protectionLines(cart);
    var subtotal = eligibleSubtotal(cart);

    if (cart.item_count === 0 && optedOut(cart)) {
      // Cart is empty — reset the opt-out so the next cart starts fresh.
      var attrs = {}; attrs[OPT_OUT_ATTR] = '';
      return { path: 'cart/update.js', payload: { attributes: attrs } };
    }
    if (lines.length && subtotal === 0) {
      var zeroAll = {};
      lines.forEach(function (l) { zeroAll[l.variant_id] = 0; });
      return { path: 'cart/update.js', payload: { updates: zeroAll } };
    }
    if (lines.length && subtotal > 0) {
      var want = feeVariant(subtotal);
      var updates = {};
      var needsFix = false;
      lines.forEach(function (l) {
        if (l.variant_id !== want.id) { updates[l.variant_id] = 0; needsFix = true; }
      });
      var keeper = lines.filter(function (l) { return l.variant_id === want.id; })[0];
      if (!keeper || keeper.quantity !== 1 || lines.length > 1) {
        updates[want.id] = 1;
        needsFix = needsFix || !keeper || keeper.quantity !== 1;
      }
      return needsFix ? { path: 'cart/update.js', payload: { updates: updates } } : null;
    }
    if (!lines.length && subtotal > 0 && cfg.defaultOn && !optedOut(cart)) {
      return { path: 'cart/add.js', payload: { items: [{ id: feeVariant(subtotal).id, quantity: 1 }] } };
    }
    return null;
  }

  /* One reconcile pass. Re-fetches the cart immediately before mutating so a
     stale event snapshot can never price against an obsolete subtotal, and
     only ever performs a single mutation per pass. */
  function reconcile(cart) {
    if (busy || failCount >= MAX_FAILURES) return;
    lastCart = cart;
    renderWidgets(cart);
    if (!computeMutation(cart)) return; // fast path: nothing to do
    busy = true;
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (fresh) {
        lastCart = fresh;
        var mutation = computeMutation(fresh);
        if (!mutation) { busy = false; return; }
        return api(mutation.path, mutation.payload).then(function () {
          failCount = 0;
          busy = false;
          return redraw();
        });
      })
      .catch(function (err) {
        // Do NOT redraw here: a redraw re-fires qjp:cart-rendered and would
        // retry the same failing mutation forever. Count and give up.
        busy = false;
        failCount++;
        console.error('[package-protection] mutation failed (' + failCount + '/' + MAX_FAILURES + '):', err && err.message ? err.message : err);
        if (failCount >= MAX_FAILURES) console.error('[package-protection] disabled for this page after repeated failures.');
      });
  }

  function scheduleReconcile(cart) {
    clearTimeout(timer);
    timer = setTimeout(function () { reconcile(cart); }, 250);
  }

  /* ---------- Widget UI (drawer foot + cart page summary) ---------- */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function buildWidget() {
    var box = document.createElement('div');
    box.className = 'pp-widget';
    box.setAttribute('data-pp-widget', '');
    box.innerHTML =
      '<label class="pp-widget__row">' +
      '<input type="checkbox" data-pp-toggle aria-describedby="pp-copy">' +
      '<span class="pp-widget__text"><strong>' + cfg.heading + '</strong> <b data-pp-fee></b><br>' +
      '<span id="pp-copy">' + cfg.copy + '</span> ' +
      '<button type="button" class="pp-widget__more" data-pp-more aria-expanded="false">Learn More</button></span>' +
      '</label>' +
      '<p class="pp-widget__expanded" data-pp-expanded hidden>' + cfg.expanded +
      (cfg.learnUrl ? ' <a href="' + cfg.learnUrl + '" target="_blank" rel="noopener">Coverage details</a>' : '') + '</p>';
    box.addEventListener('change', function (e) {
      if (!e.target.matches('[data-pp-toggle]') || busy || !lastCart) return;
      var on = e.target.checked;
      busy = true;
      var attrs = {}; attrs[OPT_OUT_ATTR] = on ? '' : '1';
      var payload = { attributes: attrs, updates: {} };
      var lines = protectionLines(lastCart);
      if (!on) {
        lines.forEach(function (l) { payload.updates[l.variant_id] = 0; });
      } else if (!lines.length) {
        var sub = eligibleSubtotal(lastCart);
        if (sub > 0) payload.updates[feeVariant(sub).id] = 1;
      }
      api('cart/update.js', payload)
        .catch(function (err) { console.error(err && err.message ? err.message : err); })
        .then(function () { busy = false; return redraw(); });
    });
    box.addEventListener('click', function (e) {
      var more = e.target.closest('[data-pp-more]');
      if (!more) return;
      var exp = box.querySelector('[data-pp-expanded]');
      exp.hidden = !exp.hidden;
      more.setAttribute('aria-expanded', exp.hidden ? 'false' : 'true');
    });
    return box;
  }
  function mountPoints() {
    var points = [];
    var foot = document.querySelector('[data-drawer-foot]');
    if (foot) points.push({ parent: foot, before: foot.firstChild });
    var summary = document.querySelector('.cart-summary');
    if (summary) points.push({ parent: summary, before: summary.firstChild });
    return points;
  }
  function renderWidgets(cart) {
    var subtotal = eligibleSubtotal(cart);
    var line = protectionLine(cart);
    mountPoints().forEach(function (pt) {
      var w = pt.parent.querySelector('[data-pp-widget]');
      if (subtotal === 0) { if (w) w.remove(); return; }
      if (!w) { w = buildWidget(); pt.parent.insertBefore(w, pt.before); }
      var toggle = w.querySelector('[data-pp-toggle]');
      var fee = w.querySelector('[data-pp-fee]');
      toggle.checked = !!line;
      fee.textContent = line ? money(line.final_line_price) : money(feeVariant(subtotal).price);
    });
  }

  /* ---------- Hooks ---------- */
  window.addEventListener('qjp:cart-rendered', function (e) {
    if (e.detail && e.detail.cart) scheduleReconcile(e.detail.cart);
  });
  // Cart page loads server-side (no drawer render event) — reconcile on load.
  function boot() {
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(scheduleReconcile)
      .catch(function () {});
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
})();
